import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

const AGE_BANDS = ['5–7', '7–9', '9–11', '12–15', '15–17', '17–20'] as const;

type AgeBand = (typeof AGE_BANDS)[number];

type RowDef = {
  test: string;
  unit: string;
  values: Record<AgeBand, string>;
};

const ROWS: RowDef[] = [
  {
    test: 'Wingate 30s anaerobic power',
    unit: 'Peak power (W/kg)',
    values: {
      '5–7': '4–6',
      '7–9': '5–7',
      '9–11': '6–9',
      '12–15': '7–11',
      '15–17': '8–13',
      '17–20': '9–15',
    },
  },
  {
    test: 'Pro agility (5-10-5)',
    unit: 'Time (s)',
    values: {
      '5–7': '6.5–8.5',
      '7–9': '5.8–7.5',
      '9–11': '5.2–6.8',
      '12–15': '4.7–6.0',
      '15–17': '4.4–5.6',
      '17–20': '4.1–5.2',
    },
  },
  {
    test: 'Aerobic capacity',
    unit: 'VO₂max (mL/kg/min)',
    values: {
      '5–7': '38–45',
      '7–9': '42–50',
      '9–11': '46–54',
      '12–15': '48–58',
      '15–17': '50–62',
      '17–20': '52–65',
    },
  },
  {
    test: 'Standing long jump',
    unit: 'Distance (cm)',
    values: {
      '5–7': '100–130',
      '7–9': '115–150',
      '9–11': '130–170',
      '12–15': '150–200',
      '15–17': '175–230',
      '17–20': '190–255',
    },
  },
  {
    test: 'Countermovement jump (CMJ)',
    unit: 'Jump height (cm)',
    values: {
      '5–7': '15–22',
      '7–9': '18–26',
      '9–11': '22–32',
      '12–15': '28–38',
      '15–17': '32–44',
      '17–20': '36–48',
    },
  },
  {
    test: 'Squat jump',
    unit: 'Jump height (cm)',
    values: {
      '5–7': '12–18',
      '7–9': '15–22',
      '9–11': '18–28',
      '12–15': '24–34',
      '15–17': '28–40',
      '17–20': '32–44',
    },
  },
  {
    test: 'Push-ups',
    unit: 'Reps / 1 min',
    values: {
      '5–7': '8–18',
      '7–9': '12–25',
      '9–11': '18–35',
      '12–15': '25–45',
      '15–17': '30–55',
      '17–20': '35–65',
    },
  },
  {
    test: 'Bench press (1RM proxy)',
    unit: 'Est. load vs body weight',
    values: {
      '5–7': 'Technique',
      '7–9': '0.25–0.35 × BW',
      '9–11': '0.35–0.50 × BW',
      '12–15': '0.50–0.70 × BW',
      '15–17': '0.65–0.85 × BW',
      '17–20': '0.80–1.05 × BW',
    },
  },
  {
    test: 'Isometric mid-thigh pull',
    unit: 'Peak / BW (ratio)',
    values: {
      '5–7': '1.8–2.4',
      '7–9': '2.2–2.8',
      '9–11': '2.6–3.2',
      '12–15': '2.9–3.6',
      '15–17': '3.2–4.0',
      '17–20': '3.5–4.4',
    },
  },
  {
    test: 'Isometric push (e.g. chest press)',
    unit: 'Peak / BW (ratio)',
    values: {
      '5–7': '0.55–0.75',
      '7–9': '0.65–0.85',
      '9–11': '0.75–0.95',
      '12–15': '0.85–1.10',
      '15–17': '0.95–1.20',
      '17–20': '1.05–1.30',
    },
  },
  {
    test: 'Pull-ups',
    unit: 'Max reps',
    values: {
      '5–7': '1–3',
      '7–9': '2–5',
      '9–11': '3–8',
      '12–15': '5–12',
      '15–17': '8–16',
      '17–20': '10–20',
    },
  },
  {
    test: 'Sit-ups / curl-ups',
    unit: 'Reps / 1 min',
    values: {
      '5–7': '18–30',
      '7–9': '25–38',
      '9–11': '30–45',
      '12–15': '35–52',
      '15–17': '40–58',
      '17–20': '42–62',
    },
  },
  {
    test: 'Grip strength',
    unit: 'Dominant hand (kg)',
    values: {
      '5–7': '14–20',
      '7–9': '18–26',
      '9–11': '24–34',
      '12–15': '32–44',
      '15–17': '40–52',
      '17–20': '48–60',
    },
  },
  {
    test: 'Flexibility (sit-and-reach)',
    unit: 'Reach (cm)',
    values: {
      '5–7': '12–22',
      '7–9': '14–24',
      '9–11': '15–26',
      '12–15': '16–28',
      '15–17': '18–30',
      '17–20': '18–32',
    },
  },
];

export function IceHockeyPhysicalBenchmarkTable() {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Illustrative academy screening bands (not federation-specific). Actual cut-offs depend on protocol, equipment, and
        governing body rules.
      </Typography>
      <TableContainer sx={{ border: '1px solid #dbe8f6', borderRadius: 1, bgcolor: '#fff', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f7fbff' }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Test</TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Typical metric</TableCell>
              {AGE_BANDS.map((band) => (
                <TableCell key={band} align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {band} yrs
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ROWS.map((row) => (
              <TableRow key={row.test}>
                <TableCell>{row.test}</TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{row.unit}</TableCell>
                {AGE_BANDS.map((band) => (
                  <TableCell key={band} align="center">
                    {row.values[band]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
